// src/context/AuthContext.js
import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import { authService, API } from '../services/api';


const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        setCurrentUser(JSON.parse(userData));
        setIsAuthenticated(true);
      } catch (e) {
        console.error('Erreur lors du chargement des données utilisateur:', e);
      }
    }
    
    setLoading(false);
  }, []);

    // Add this function inside your AuthProvider component

  const resendVerificationEmail = async () => {
    try {
      const response = await API.post('/auth/resend-verification');
      console.log('Resend verification response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Resend verification error:', error);
      throw error;
    }
  };


  const persistAuthUser = useCallback((user) => {
    if (!user) {
      return;
    }
    const normalizedUser = typeof user.toJSON === 'function' ? user.toJSON() : { ...user };
    localStorage.setItem('userData', JSON.stringify(normalizedUser));
    setCurrentUser((prev) => {
      if (prev && prev._id === normalizedUser._id) {
        return { ...prev, ...normalizedUser };
      }
      return normalizedUser;
    });
    setIsAuthenticated(true);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getProfile();
      if (response?.success && response.user) {
        persistAuthUser(response.user);
        return { success: true, user: response.user };
      }
      return { success: false, error: response?.error || 'Impossible de récupérer le profil utilisateur' };
    } catch (err) {
      console.error('AuthContext - Erreur lors de la mise à jour du profil:', err);
      return {
        success: false,
        error: err.response?.data?.error || err.message || 'Erreur lors de la récupération du profil'
      };
    }
  }, [persistAuthUser]);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      refreshUser().catch(() => {});
    }
  }, [loading, isAuthenticated, refreshUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    const handleFocus = () => {
      refreshUser().catch(() => {});
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated, refreshUser]);

// Fonction de connexion
  const login = async (email, password) => {
    setError(null);
    try {
      console.log('AuthContext - Tentative de connexion avec:', { email });
      
      // Faire un appel API réel
      const response = await authService.login({ email, password });
      console.log('AuthContext - Réponse de l\'API:', response);
      
      if (!response.success) {
        throw new Error(response.error || 'Échec de la connexion');
      }
      
      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }
      persistAuthUser(response.user);
      
      // Charger les préférences utilisateur après login
      try {
        const prefRes = await API.get('/user-preferences/me');
        if (prefRes.data && prefRes.data.preferences) {
          localStorage.setItem('filterPreferences', JSON.stringify(prefRes.data.preferences));
        }
      } catch (e) {
        // Silencieux si pas de préférences
      }
      
      return { success: true };
    } catch (err) {
      console.error('AuthContext - Erreur de connexion:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Erreur de connexion';
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  // Fonction d'inscription
  const register = async (userData) => {
    setError(null);
    try {
      console.log('Tentative d\'inscription avec:', { email: userData.email });
      
      const response = await authService.register(userData);
      
      if (!response?.success) {
        throw new Error(response?.error || 'Inscription impossible');
      }
      
      if (response.token) {
        localStorage.setItem('authToken', response.token);
      }
      persistAuthUser(response.user);
      return { success: true };
    } catch (err) {
      console.error('Erreur d\'inscription:', err);
      const errorMessage = err.response?.data?.error || 'Erreur lors de l\'inscription';
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    setCurrentUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    currentUser,
    loading,
    error,
    isAuthenticated,
    login,
    register,
    logout,
    refreshUser,
    resendVerificationEmail, 
    updateUser: persistAuthUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;